import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import {
  Alert,
  backLinkClasses,
  Badge,
  Button,
  Card,
  DataList,
  DataRow,
  NotProvided,
  Page,
  PageHeader,
  isNotFound,
  NotFoundError,
} from '@evogard/ui';
import type { ChangeSubject } from '@evogard/contract';
import { canAccess, formatDate, loadFailureMessage } from '@evogard/domain';
import { backSearch, useTablePage } from '../../lib/list-search.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { RefreshOutcome, useErpRefresh } from './common.js';
import { ChangesTable, fieldLabel, RecordLink, SUBJECT_LABEL } from './history.js';
import { MissingRecord } from '../../components/MissingRecord.js';

/**
 * UMA alteração do SGA — a página que o botão "Ver" da lista abre
 * (14/10/2026): o campo, de quanto para quanto, quem e quando, o registro
 * alterado com link para a ficha, e as outras mudanças do MESMO registro.
 *
 * O "Atualizar do ERP" daqui atualiza o REGISTRO (o boleto, o associado ou o
 * veículo) — é o valor de agora que quem olha uma alteração quer conferir. As
 * alterações em si chegam pela esteira, ou pelo botão da lista.
 */
export function ChangeScreen() {
  const { subject, code } = useParams({
    from: '/authenticated/erp/alteracoes/$subject/$code',
  });
  /* a página de cada tabela desta ficha, na URL — o "voltar" devolve a página */
  const pages = useSearch({ from: '/authenticated/erp/alteracoes/$subject/$code' });
  const historyPage = useTablePage('historico', pages.historico);
  const { data: context } = useSessionContext();
  const canRefresh = canAccess(context?.permissions ?? [], 'erp.sync', 'executar');

  const queryKey = ['erp', 'alteracao', subject, code] as const;
  const { mutation: refresh, follow } = useErpRefresh(queryKey, () =>
    c?.refreshCode ? { entity: c.subject, id: c.refreshCode } : null,
  );

  const query = useQuery({
    refetchInterval: follow,
    queryKey,
    queryFn: async () => {
      const r = await api.erp.change({
        params: { subject: subject as ChangeSubject, code },
      });
      if (r.status === 404)
        throw new NotFoundError('Esta alteração não existe no cadastro desta base');
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'a alteração'));
      return r.body;
    },
    retry: false,
  });
  const c = query.data;

  if (isNotFound(query.error)) {
    return (
      <MissingRecord
        title={query.error.message}
        list={{ to: '/erp/alteracoes', label: 'Ver as alterações' }}
      />
    );
  }

  return (
    <Page measure="leitura">
      <Link
        to="/erp/alteracoes"
        search={backSearch('/erp/alteracoes') as never}
        className={backLinkClasses()}
      >
        Alterações
      </Link>

      <PageHeader
        title={c ? `${fieldLabel(c.field)} — ${SUBJECT_LABEL[c.subject]}` : 'Alteração'}
      >
        {c && `Alteração ${c.erpCode} no ERP`}
        {c?.changedOn && ` · ${formatDate(c.changedOn)}`}
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {c && canRefresh && c.refreshCode && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            icon="atualizar"
          >
            {refresh.isPending ? 'Atualizando…' : 'Atualizar do ERP'}
          </Button>
          <span className="text-sm text-texto-suave">
            traz o {SUBJECT_LABEL[c.subject].toLowerCase()} como ele está agora no ERP
          </span>
        </div>
      )}

      <RefreshOutcome mutation={refresh} />

      {c && (
        <Card title="A alteração">
          <DataList>
            <DataRow label="Registro">
              <span className="inline-flex items-center gap-2">
                <Badge tone="neutro">{SUBJECT_LABEL[c.subject]}</Badge>
                <RecordLink change={c} />
              </span>
            </DataRow>
            <DataRow label="Campo">{fieldLabel(c.field)}</DataRow>
            <DataRow label="Antes">{shown(c.oldValue, c.oldLabel)}</DataRow>
            <DataRow label="Depois">
              <strong className="font-medium">{shown(c.newValue, c.newLabel)}</strong>
            </DataRow>
            <DataRow label="Quando">
              {c.changedOn ? formatDate(c.changedOn) : <NotProvided />}
            </DataRow>
            <DataRow label="Quem">{c.author ?? <NotProvided />}</DataRow>
            <DataRow label="Observação">{c.note ?? <NotProvided />}</DataRow>
          </DataList>
        </Card>
      )}

      {c && (
        <Card
          title={`Alterações deste ${SUBJECT_LABEL[c.subject].toLowerCase()} (${c.history.length})`}
          descricao="da mais recente para a mais antiga"
          bleed
        >
          <ChangesTable
            items={c.history}
            paging={historyPage}
            openable={false}
            empty="Nenhuma alteração deste registro."
          />
        </Card>
      )}
    </Page>
  );
}

function shown(value: string | null, label: string | null) {
  if (value === null || value === '') return <NotProvided>vazio</NotProvided>;
  return label === null ? value : `${label} (${value})`;
}
